import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson } from './client'
import type { ApprovalState } from './changeManagement'

/**
 * Sedintele CAB (v4/api, `App\Easeedesk\Change\CabMeetings`): data,
 * participantii, change-urile de pe agenda si hotararea pe fiecare, cu minuta.
 *
 * Numele campurilor sunt exact cele pe care le scrie serverul.
 */

export type MeetingStatus = 'planned' | 'held' | 'cancelled'
export type MeetingDecision = 'approve' | 'reject' | 'defer' | ''
export type MeetingScope = 'upcoming' | 'past' | 'all'

export interface Option<V = string> {
  value: V
  label: string
}

export interface CabMeetingRow {
  ID: number
  title: string
  meeting_at: string
  location: string
  status: MeetingStatus
  status_label: string
  cab_group_id: number
  cab_group_name: string
  items: number
  decided: number
}

export interface CabMeetingList {
  scope: MeetingScope
  meetings: CabMeetingRow[]
  canEdit: boolean
  groups: { ID: number; name: string }[]
  staff: { ID: number; name: string }[]
  statuses: Option[]
}

export interface AgendaItem {
  ticket_id: number
  decision: MeetingDecision
  decision_label: string
  notes: string
  decided_by_name: string
  decided_at: string
  decision_round: number | null
  hidden: boolean
  subject?: string
  status?: string
  status_color?: string
  approval_state?: ApprovalState
  approval_round?: number
  class_label?: string
  risk_label?: string
  planned_start?: string
  canVote?: boolean
}

export interface AgendaCandidate {
  ticket_id: number
  subject: string
  company_id: number
  approval_state: ApprovalState
  change_class: number
  planned_start: string
}

export interface CabMeetingDetail {
  meeting: {
    ID: number
    title: string
    meeting_at: string
    location: string
    status: MeetingStatus
    status_label: string
    cab_group_id: number
    cab_group_name: string
    minutes: string
  }
  participants: { user_id: number; name: string; attended: boolean }[]
  agenda: AgendaItem[]
  candidates: AgendaCandidate[]
  groups: { ID: number; name: string }[]
  staff: { ID: number; name: string }[]
  statuses: Option[]
  decisions: Option[]
  canEdit: boolean
}

export interface MeetingAnswer {
  status?: string
  id?: number
  message: string
  state?: ApprovalState
  voted?: boolean
}

export interface MeetingForm {
  title: string
  meeting_at: string
  location: string
  cab_group_id: number
  participants: number[]
}

export function cabMeetingsQuery(scope: MeetingScope) {
  return queryOptions({
    queryKey: ['easeedesk', 'cab_meetings', scope],
    queryFn: () => apiGet<CabMeetingList>(`/easeedesk/cab_meetings?scope=${scope}`),
  })
}

export function cabMeetingQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'cab_meeting', id],
    queryFn: () => apiGet<CabMeetingDetail>(`/easeedesk/cab_meetings/${id}`),
  })
}

export function createCabMeeting(form: MeetingForm) {
  return apiPostJson<MeetingAnswer>('/easeedesk/cab_meetings', form)
}

export function saveCabMeeting(
  id: number,
  fields: Partial<MeetingForm> & { status?: MeetingStatus; minutes?: string; attended?: number[] },
) {
  return apiPostJson<MeetingAnswer>(`/easeedesk/cab_meetings/${id}`, fields)
}

export function removeCabMeeting(id: number) {
  return apiPost<MeetingAnswer>(`/easeedesk/cab_meetings/${id}/remove`, {})
}

export function addToAgenda(id: number, ticket: number) {
  return apiPost<MeetingAnswer>(`/easeedesk/cab_meetings/${id}/agenda`, { ticket })
}

export function removeFromAgenda(id: number, ticket: number) {
  return apiPost<MeetingAnswer>(`/easeedesk/cab_meetings/${id}/agenda/${ticket}/remove`, {})
}

export function decideOnAgenda(id: number, ticket: number, decision: MeetingDecision, notes: string, castVote: boolean) {
  return apiPostJson<MeetingAnswer>(`/easeedesk/cab_meetings/${id}/agenda/${ticket}/decision`, {
    decision,
    notes,
    cast_vote: castVote,
  })
}
